import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import * as authApi from '../api/auth';
import * as usersApi from '../api/users';
import { configureApiClient } from '../api/client';
import {
  configureTaskResolver,
  clearAllTasks,
  reconnectTaskResolverAfterTokenChange,
} from '../services/taskResolver';

const AUTH_STORAGE_KEY = 'dxliving_auth';
const TOKEN_REFRESH_BUFFER_MS = 5 * 60 * 1000;
const TOKEN_REFRESH_MIN_DELAY_MS = 15 * 1000;

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

function loadStoredAuth() {
  try {
    const raw = sessionStorage.getItem(AUTH_STORAGE_KEY) || localStorage.getItem(AUTH_STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {
    // ignore
  }
  return null;
}

function saveAuth(data, rememberMe) {
  const storage = rememberMe ? localStorage : sessionStorage;
  storage.setItem(AUTH_STORAGE_KEY, JSON.stringify(data));
  // clear from the other store
  (rememberMe ? sessionStorage : localStorage).removeItem(AUTH_STORAGE_KEY);
}

function clearStoredAuth() {
  sessionStorage.removeItem(AUTH_STORAGE_KEY);
  localStorage.removeItem(AUTH_STORAGE_KEY);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showPreloader, setShowPreloader] = useState(false);
  const [preloaderFadingOut, setPreloaderFadingOut] = useState(false);
  const [sessionExpiredToastVisible, setSessionExpiredToastVisible] = useState(false);
  const refreshInFlightRef = useRef(null);
  const refreshTimerRef = useRef(null);
  const sessionExpiredToastTimerRef = useRef(null);

  const setAuth = useCallback((data, rememberMe = false) => {
    if (data) {
      setAccessToken(data.accessToken);
      setUser(data.user ?? null);
      saveAuth(data, rememberMe);
    } else {
      setAccessToken(null);
      setUser(null);
      clearStoredAuth();
    }
  }, []);

  const login = useCallback(async (credentials, rememberMe = false) => {
    const data = await authApi.login(credentials);
    setAuth(
      {
        accessToken: data.accessToken,
        refreshToken: data.refreshToken,
        expiresAt: data.expiresAt,
        user: data.user,
      },
      rememberMe
    );
    setShowPreloader(true);
  }, [setAuth]);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      /* ignore - clear local state regardless */
    } finally {
      setAuth(null);
    }
  }, [setAuth]);

  const showSessionExpiredToast = useCallback(() => {
    setSessionExpiredToastVisible(true);
    if (sessionExpiredToastTimerRef.current) {
      clearTimeout(sessionExpiredToastTimerRef.current);
    }
    sessionExpiredToastTimerRef.current = setTimeout(() => {
      setSessionExpiredToastVisible(false);
      sessionExpiredToastTimerRef.current = null;
    }, 5000);
  }, []);

  const refreshAccessToken = useCallback(async () => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }
    const stored = loadStoredAuth();
    if (!stored?.refreshToken) {
      setAuth(null);
      return null;
    }
    refreshInFlightRef.current = (async () => {
      const runRefresh = async (refreshToken, fallbackUser) => {
        const data = await authApi.refreshToken(refreshToken);
        const next = {
          accessToken: data.accessToken,
          refreshToken: data.refreshToken ?? refreshToken,
          expiresAt: data.expiresAt,
          user: data.user ?? fallbackUser ?? null,
        };
        const rememberMe = !!localStorage.getItem(AUTH_STORAGE_KEY);
        saveAuth(next, rememberMe);
        setAccessToken(next.accessToken);
        setUser(next.user);
        return next.accessToken;
      };
      try {
        // authApi.refreshToken expects the raw refresh token string
        return await runRefresh(stored.refreshToken, stored.user);
      } catch {
        // Possible rotation/race: another request/tab may have already refreshed and
        // persisted a newer refresh token. Retry once with latest stored token.
        const latest = loadStoredAuth();
        const latestRefresh = latest?.refreshToken;
        const latestAccess = latest?.accessToken;
        const latestExpiryTs = latest?.expiresAt ? new Date(latest.expiresAt).getTime() : 0;
        const hasFreshAccess =
          Boolean(latestAccess) &&
          Number.isFinite(latestExpiryTs) &&
          latestExpiryTs > Date.now() + 15_000;
        if (latestRefresh && latestRefresh !== stored.refreshToken) {
          if (hasFreshAccess) {
            setAccessToken(latestAccess);
            setUser(latest?.user ?? null);
            return latestAccess;
          }
          try {
            return await runRefresh(latestRefresh, latest?.user ?? stored.user);
          } catch {
            // fall through to auth lost
          }
        }
        showSessionExpiredToast();
        setAuth(null);
        return null;
      }
    })();
    try {
      return await refreshInFlightRef.current;
    } finally {
      refreshInFlightRef.current = null;
    }
  }, [setAuth, showSessionExpiredToast]);

  // Keep API client config in sync with latest auth on every render (avoids races on first request).
  configureApiClient({
    getToken: () => accessToken,
    refreshFn: refreshAccessToken,
    onLogout: () => {
      showSessionExpiredToast();
      setAuth(null);
    },
  });

  // Configure task resolver (WebSocket) — same pattern as reference: pass getter
  // so getWsUrl() always reads the current token. Reconnect WS after token
  // refresh so `?token=` matches (stale socket would still use old query string).
  useEffect(() => {
    configureTaskResolver({ getAccessToken: () => accessToken });
    if (accessToken) {
      reconnectTaskResolverAfterTokenChange();
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) clearAllTasks();
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;
    usersApi
      .getCurrentUser()
      .then((profile) => setUser(profile))
      .catch(() => { /* keep existing user from login/restore */ });
  }, [accessToken]);

  useEffect(() => {
    const stored = loadStoredAuth();
    if (!stored) {
      setIsLoading(false);
      return;
    }
    const rememberMe = !!localStorage.getItem(AUTH_STORAGE_KEY);
    // If we have a valid (non-expired) access token, restore immediately
    const expiresAt = stored.expiresAt ? new Date(stored.expiresAt).getTime() : 0;
    const isValid = stored.accessToken && expiresAt > Date.now() + 60_000; // 1 min buffer
    if (isValid) {
      setAccessToken(stored.accessToken);
      setUser(stored.user ?? null);
      setIsLoading(false);
      return;
    }
    // Otherwise refresh to get new tokens
    if (!stored.refreshToken) {
      clearStoredAuth();
      setIsLoading(false);
      return;
    }
    authApi
      // authApi.refreshToken expects the raw refresh token string
      .refreshToken(stored.refreshToken)
      .then((data) => {
        setAuth(
          {
            accessToken: data.accessToken,
            refreshToken: data.refreshToken ?? stored.refreshToken,
            expiresAt: data.expiresAt,
            user: data.user ?? stored.user,
          },
          rememberMe
        );
      })
      .catch(() => {
        clearStoredAuth();
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [setAuth]);

  useEffect(() => {
    if (!showPreloader) return;
    setPreloaderFadingOut(false);
    const fadeOutT = setTimeout(() => setPreloaderFadingOut(true), 4500);
    const hideT = setTimeout(() => setShowPreloader(false), 5000);
    return () => {
      clearTimeout(fadeOutT);
      clearTimeout(hideT);
    };
  }, [showPreloader]);

  useEffect(() => () => {
    if (sessionExpiredToastTimerRef.current) {
      clearTimeout(sessionExpiredToastTimerRef.current);
      sessionExpiredToastTimerRef.current = null;
    }
  }, []);

  // Proactively refresh before token expiry so active sessions stay signed in.
  useEffect(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (!accessToken) return undefined;
    const stored = loadStoredAuth();
    const expiresAtTs = stored?.expiresAt ? new Date(stored.expiresAt).getTime() : 0;
    if (!Number.isFinite(expiresAtTs) || expiresAtTs <= 0) return undefined;
    const refreshInMs = Math.max(
      TOKEN_REFRESH_MIN_DELAY_MS,
      expiresAtTs - Date.now() - TOKEN_REFRESH_BUFFER_MS
    );
    refreshTimerRef.current = setTimeout(() => {
      void refreshAccessToken();
    }, refreshInMs);
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [accessToken, refreshAccessToken]);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!accessToken,
        user,
        accessToken,
        isLoading,
        showPreloader,
        preloaderFadingOut,
        login,
        logout,
        refreshAccessToken,
      }}
    >
      {children}
      {sessionExpiredToastVisible && (
        <div className="pointer-events-none fixed top-4 left-1/2 z-[200] -translate-x-1/2 rounded-lg border border-gray-200 bg-white/95 px-4 py-2 text-sm font-medium text-gray-800 shadow-lg backdrop-blur-sm">
          Session expired, please sign in again.
        </div>
      )}
    </AuthContext.Provider>
  );
};
